/**
 * Number Schema Create/Edit Dialog
 */
import { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Info } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  useCreateNumberSchema,
  useUpdateNumberSchema,
  usePreviewNumber,
  useNumberSchema,
} from "../hooks/use-number-schema";
import {
  SCHEMA_TYPE_LABELS,
  COUNTER_RESET_LABELS,
  TEMPLATE_PLACEHOLDERS,
  type NumberSchema,
  type SchemaType,
} from "../types/number-schema.types";

const formSchema = z.object({
  schemaType: z.enum(["invoice", "customer", "offer", "delivery"]),
  name: z.string().min(1, "Name ist erforderlich").max(255),
  description: z.string().optional(),
  template: z
    .string()
    .min(1, "Template ist erforderlich")
    .max(500)
    .refine(
      (val) => {
        const numberMatches = val.match(/\{\{NUMBER(?::\d+)?\}\}/g);
        return !numberMatches || numberMatches.length === 1;
      },
      { message: "NUMBER kann nur einmal im Template verwendet werden" },
    ),
  counterStart: z.coerce.number().int().min(1).optional(),
  counterReset: z.enum(["never", "yearly", "monthly", "daily", "per_customer"]),
  isDefault: z.boolean().optional(),
  isActive: z.boolean().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface NumberSchemaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schema?: NumberSchema | null;
  defaultType?: SchemaType;
}

export function NumberSchemaDialog({
  open,
  onOpenChange,
  schema,
  defaultType = "invoice",
}: NumberSchemaDialogProps) {
  const { toast } = useToast();
  const createMutation = useCreateNumberSchema();
  const updateMutation = useUpdateNumberSchema();
  const templateInputRef = useRef<HTMLInputElement>(null);
  const [cursorPosition, setCursorPosition] = useState<number>(0);

  // Load full schema details when editing (to get isUsed flag)
  const { data: schemaDetails } = useNumberSchema(schema?.id || "");
  const currentSchema = schemaDetails || schema;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      schemaType: defaultType,
      name: "",
      description: "",
      template: "RE-{{YEAR}}-{{NUMBER:5}}",
      counterStart: 1,
      counterReset: "yearly",
      isDefault: false,
      isActive: true,
    },
  });

  const schemaType = form.watch("schemaType");
  const template = form.watch("template");
  const [debouncedTemplate, setDebouncedTemplate] = useState(template);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTemplate(template);
    }, 300);

    return () => clearTimeout(timer);
  }, [template]);

  // Preview next number
  const { data: previewData } = usePreviewNumber(
    schemaType,
    undefined,
    "K-00001",
    "Musterfirma",
    debouncedTemplate,
  );

  // Reset form when schema or dialog state changes
  useEffect(() => {
    if (open) {
      if (schema) {
        form.reset({
          schemaType: schema.schemaType,
          name: schema.name,
          description: schema.description || "",
          template: schema.template,
          counterStart: schema.counterStart,
          counterReset: schema.counterReset,
          isDefault: schema.isDefault,
          isActive: schema.isActive,
        });
      } else {
        form.reset({
          schemaType: defaultType,
          name: "",
          description: "",
          template: "RE-{{YEAR}}-{{NUMBER:5}}",
          counterStart: 1,
          counterReset: "yearly",
          isDefault: false,
          isActive: true,
        });
      }
    }
  }, [open, schema, defaultType, form]);

  const onSubmit = async (values: FormValues) => {
    try {
      if (schema?.id) {
        // Update existing
        await updateMutation.mutateAsync({
          id: schema.id,
          data: {
            name: values.name,
            description: values.description,
            template: values.template,
            counterStart: values.counterStart,
            counterReset: values.counterReset,
            isDefault: values.isDefault,
            isActive: values.isActive,
          },
        });
        toast({
          title: "Erfolgreich aktualisiert",
          description: "Das Schema wurde aktualisiert.",
        });
      } else {
        // Create new
        await createMutation.mutateAsync({
          schemaType: values.schemaType,
          name: values.name,
          description: values.description,
          template: values.template,
          counterStart: values.counterStart,
          counterReset: values.counterReset,
          isDefault: values.isDefault,
          isActive: values.isActive,
        });
        toast({
          title: "Erfolgreich erstellt",
          description: "Das Schema wurde erstellt.",
        });
      }
      // Close dialog only after successful mutation
      onOpenChange(false);
    } catch (error) {
      // Show specific error message from server
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Das Schema konnte nicht gespeichert werden.";

      toast({
        title: "Fehler",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const groupedPlaceholders = TEMPLATE_PLACEHOLDERS.reduce(
    (acc, placeholder) => {
      const category = placeholder.category;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category]!.push(placeholder);
      return acc;
    },
    {} as Record<string, Array<(typeof TEMPLATE_PLACEHOLDERS)[number]>>,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {schema?.id
              ? currentSchema?.isUsed
                ? "Schema ansehen"
                : "Schema bearbeiten"
              : "Neues Schema erstellen"}
          </DialogTitle>
          <DialogDescription>
            {currentSchema?.isUsed
              ? "Dieses Schema wird bereits verwendet und kann nicht mehr bearbeitet werden."
              : "Erstellen Sie ein Template für die automatische Nummernvergabe"}
          </DialogDescription>
        </DialogHeader>

        {currentSchema?.isUsed && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Dieses Schema wurde bereits für die Nummernvergabe verwendet und
              kann daher nicht mehr bearbeitet werden. Sie können nur noch den
              Status "Aktiv" ändern, um das Schema zu deaktivieren.
            </AlertDescription>
          </Alert>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Schema Type */}
            {!schema?.id && (
              <FormField
                control={form.control}
                name="schemaType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Typ</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.entries(SCHEMA_TYPE_LABELS).map(
                          ([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Name */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="z.B. Standard Rechnungsnummer"
                      disabled={currentSchema?.isUsed}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Beschreibung (optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Beschreibung des Schemas"
                      disabled={currentSchema?.isUsed}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Template */}
            <FormField
              control={form.control}
              name="template"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Template</FormLabel>
                  <FormControl>
                    <Input
                      ref={(e) => {
                        field.ref(e);
                        templateInputRef.current = e;
                      }}
                      placeholder="z.B. RE-{{YEAR}}-{{NUMBER:5}}"
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      onSelect={(e) => {
                        const target = e.target as HTMLInputElement;
                        setCursorPosition(target.selectionStart || 0);
                      }}
                      onClick={(e) => {
                        const target = e.target as HTMLInputElement;
                        setCursorPosition(target.selectionStart || 0);
                      }}
                      onKeyUp={(e) => {
                        const target = e.target as HTMLInputElement;
                        setCursorPosition(target.selectionStart || 0);
                      }}
                      className="font-mono"
                      disabled={currentSchema?.isUsed}
                    />
                  </FormControl>
                  <FormDescription>
                    {previewData?.preview && (
                      <span className="text-sm">
                        Vorschau:{" "}
                        <code className="bg-muted rounded px-2 py-1">
                          {previewData.preview}
                        </code>
                      </span>
                    )}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Placeholder Helper */}
            <div className="space-y-3 rounded-lg border p-4">
              <h4 className="text-sm font-medium">Platzhalter einfügen</h4>
              <div className="grid gap-4">
                {Object.entries(groupedPlaceholders).map(
                  ([category, placeholders]) => (
                    <div key={category}>
                      <p className="mb-2 text-sm font-medium capitalize">
                        {category}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {placeholders.map((p) => {
                          const currentTemplate = form.watch("template");
                          // Disable NUMBER placeholders if any NUMBER placeholder already exists
                          const isNumberPlaceholder =
                            p.key.includes("{{NUMBER");
                          const hasNumberPlaceholder =
                            currentTemplate.includes("{{NUMBER");
                          const isDisabled =
                            currentSchema?.isUsed ||
                            (isNumberPlaceholder && hasNumberPlaceholder);

                          return (
                            <Badge
                              key={p.key}
                              variant="outline"
                              className={`cursor-pointer ${
                                isDisabled
                                  ? "cursor-not-allowed opacity-50"
                                  : "hover:bg-primary hover:text-primary-foreground"
                              }`}
                              onMouseDown={(e) => {
                                e.preventDefault(); // Prevent focus loss
                                if (isDisabled) return;

                                const input = templateInputRef.current;
                                if (!input) return;

                                const current = form.getValues("template");
                                const pos = cursorPosition;

                                const newValue =
                                  current.substring(0, pos) +
                                  p.key +
                                  current.substring(pos);
                                const newPos = pos + p.key.length;

                                // Update using form.setValue
                                form.setValue("template", newValue, {
                                  shouldValidate: false,
                                  shouldDirty: true,
                                });

                                // Focus and set cursor in next tick
                                setTimeout(() => {
                                  input.focus();
                                  input.setSelectionRange(newPos, newPos);
                                  setCursorPosition(newPos);
                                }, 0);
                              }}
                              title={
                                isDisabled
                                  ? "NUMBER kann nur einmal verwendet werden"
                                  : p.description
                              }
                            >
                              {p.key}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Counter Start */}
              <FormField
                control={form.control}
                name="counterStart"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start-Nummer</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={1}
                        disabled={currentSchema?.isUsed}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Counter Reset */}
              <FormField
                control={form.control}
                name="counterReset"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Zähler zurücksetzen</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      disabled={currentSchema?.isUsed}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.entries(COUNTER_RESET_LABELS).map(
                          ([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Is Default */}
            <FormField
              control={form.control}
              name="isDefault"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Standard-Schema</FormLabel>
                    <FormDescription>
                      Als Standard für neue{" "}
                      {SCHEMA_TYPE_LABELS[schemaType].toLowerCase()}nummern
                      verwenden
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={currentSchema?.isUsed}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Is Active */}
            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Aktiv</FormLabel>
                    <FormDescription>
                      Schema ist aktiv und kann verwendet werden
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {currentSchema?.isUsed ? "Schließen" : "Abbrechen"}
              </Button>
              {!currentSchema?.isUsed && (
                <Button type="submit">
                  {schema?.id ? "Aktualisieren" : "Erstellen"}
                </Button>
              )}
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
